import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { RiLockPasswordLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import { api } from '../../services/api';
import { brandLogo } from '../../utils/brandLogos';

// ==================== ANIMATIONS ====================
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

const checkmark = keyframes`
  0% { stroke-dashoffset: 50; }
  100% { stroke-dashoffset: 0; }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-5px); }
  40%, 80% { transform: translateX(5px); }
`;

// ==================== STYLED COMPONENTS ====================
const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #318CD5 0%, #4FACFE 100%);
  padding: 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(79, 172, 254, 0.08) 0%, transparent 50%);
    animation: ${pulse} 8s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    right: -30%;
    width: 80%;
    height: 80%;
    background: radial-gradient(circle, rgba(49, 140, 213, 0.1) 0%, transparent 60%);
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 1000px;
  min-height: 580px;
  display: flex;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 24px;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  overflow: hidden;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 420px;
    min-height: auto;
  }
`;

const BrandSide = styled.div`
  width: 50%;
  background: linear-gradient(145deg, #f8fafc 0%, #e8f4fd 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  position: relative;
  overflow: hidden;
  animation: ${slideInLeft} 0.6s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background:
      radial-gradient(circle at 20% 80%, rgba(49, 140, 213, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(79, 172, 254, 0.08) 0%, transparent 50%);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Logo = styled.img`
  width: 280px;
  height: auto;
  margin-bottom: 32px;
  animation: ${float} 4s ease-in-out infinite;
  filter: drop-shadow(0 4px 12px rgba(49, 140, 213, 0.2));
  position: relative;
  z-index: 1;
`;

const BrandTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #074D82;
  text-align: center;
  margin-bottom: 12px;
  letter-spacing: -0.5px;
  position: relative;
  z-index: 1;
`;

const BrandSubtitle = styled.p`
  font-size: 18px;
  font-weight: 500;
  color: #318CD5;
  text-align: center;
  max-width: 300px;
  line-height: 1.5;
  position: relative;
  z-index: 1;
`;

const FormSide = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  animation: ${slideInRight} 0.6s ease-out;

  @media (max-width: 768px) {
    width: 100%;
    padding: 40px 24px;
  }
`;

const MobileLogo = styled.img`
  display: none;
  width: 200px;
  height: auto;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    display: block;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 340px;
`;

const ContentSection = styled.div`
  animation: ${fadeIn} 0.6s ease-out 0.1s both;
  margin-bottom: 28px;
`;

const Title = styled.h2`
  font-size: 26px;
  font-weight: 700;
  color: #1a365d;
  margin-bottom: 12px;
`;

const Description = styled.p`
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  position: relative;
  animation: ${fadeIn} 0.6s ease-out ${props => 0.2 + props.$delay * 0.1}s both;

  ${props => props.$hasError && `
    animation: ${shake} 0.4s ease-out;
  `}
`;

const InputIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
  transition: color 0.3s ease;
  z-index: 2;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 54px;
  padding: 0 48px;
  border: 2px solid ${props => props.$hasError ? '#ef4444' : '#e2e8f0'};
  border-radius: 14px;
  font-size: 15px;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.3s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ef4444' : '#318CD5'};
    background: #ffffff;
    box-shadow: 0 0 0 4px ${props => props.$hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(49, 140, 213, 0.1)'};
  }

  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px #f8fafc inset;
    -webkit-text-fill-color: #1e293b;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  z-index: 2;

  &:hover {
    color: #318CD5;
  }
`;

const PasswordStrength = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;
`;

const StrengthBar = styled.div`
  height: 4px;
  flex: 1;
  border-radius: 2px;
  background: ${props => {
    if (props.$active) {
      if (props.$strength === 'weak') return '#ef4444';
      if (props.$strength === 'medium') return '#f59e0b';
      if (props.$strength === 'strong') return '#10b981';
    }
    return '#e2e8f0';
  }};
  transition: background 0.3s ease;
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 54px;
  background: linear-gradient(135deg, #318CD5 0%, #4FACFE 100%);
  border: none;
  border-radius: 14px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out 0.5s both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px -5px rgba(49, 140, 213, 0.5);

    &::before {
      opacity: 1;
      animation: ${shimmer} 1.5s infinite;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const MessageBox = styled.div`
  padding: 16px;
  border-radius: 12px;
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: ${fadeIn} 0.4s ease-out;

  ${props => props.$success ? `
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%);
    border: 1px solid rgba(16, 185, 129, 0.3);
    color: #059669;
  ` : `
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #dc2626;
  `}
`;

const MessageIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${props => props.$success ? `
    background: rgba(16, 185, 129, 0.2);
  ` : `
    background: rgba(239, 68, 68, 0.2);
  `}

  svg {
    width: 14px;
    height: 14px;
  }
`;

const MessageText = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
`;

const SuccessCheckmark = styled.svg`
  stroke: currentColor;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;

  path {
    stroke-dasharray: 50;
    animation: ${checkmark} 0.5s ease-out forwards;
  }
`;

// ==================== COMPONENT ====================
const RedefineSenhaPage = () => {
  const { t } = useTranslation('auth');
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const getPasswordStrength = (pwd) => {
    if (pwd.length === 0) return null;
    if (pwd.length < 6) return 'weak';
    if (pwd.length < 10 || !/[A-Z]/.test(pwd) || !/[0-9]/.test(pwd)) return 'medium';
    return 'strong';
  };

  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (password !== confirmPassword) {
      setError(t('toasts.passwordMismatch'));
      toast(t('toasts.passwordMismatch'), { autoClose: 2000, type: 'error' });
      return;
    }

    if (password.length < 6) {
      setError(t('toasts.passwordTooShort'));
      toast(t('toasts.passwordTooShort'), { autoClose: 2000, type: 'error' });
      return;
    }

    setLoading(true);

    try {
      const response = await api.post(`/reset-password/${token}`, {
        newPassword: password,
        confirmPassword: confirmPassword,
      });

      if (response.status === 200) {
        setMessage(response.data.msg || t('toasts.passwordResetSuccess'));
        setIsSuccess(true);
        toast(response.data.msg || t('toasts.passwordResetSuccess'), { autoClose: 3000, type: 'success' });
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMessage(response.data.msg || t('toasts.passwordResetError'));
        setIsSuccess(false);
        toast(response.data.msg || t('toasts.passwordResetError'), { autoClose: 2000, type: 'error' });
      }
    } catch (error) {
      if (error.response && error.response.data) {
        setError(error.response.data.msg || t('toasts.passwordResetError'));
        toast(error.response.data.msg || t('toasts.passwordResetError'), { autoClose: 2000, type: 'error' });
      } else {
        setError(t('toasts.serverError'));
        toast(t('toasts.serverError'), { autoClose: 2000, type: 'error' });
      }
      setIsSuccess(false);
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error(error);
    }

    setLoading(false);
  };

  return (
    <PageContainer>
      <Card>
        <BrandSide>
          <Logo src={brandLogo} alt={t('login.logoAlt')} />
          <BrandTitle>{t('login.title')}</BrandTitle>
          <BrandSubtitle>{t('login.subtitle')}</BrandSubtitle>
        </BrandSide>

        <FormSide>
          <FormContainer>
            <MobileLogo src={brandLogo} alt={t('login.logoAlt')} />

            <ContentSection>
              <Title>{t('resetPassword.title')}</Title>
              <Description>{t('resetPassword.instruction')}</Description>
            </ContentSection>

            <Form onSubmit={handleSubmit}>
              <InputGroup $delay={0} $hasError={error && password.length < 6}>
                <StyledInput
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('resetPassword.newPasswordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  $hasError={error && password.length < 6}
                  required
                />
                <InputIcon>
                  <RiLockPasswordLine />
                </InputIcon>
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </PasswordToggle>
              </InputGroup>

              {password.length > 0 && (
                <PasswordStrength>
                  <StrengthBar $active={true} $strength={passwordStrength} />
                  <StrengthBar $active={passwordStrength === 'medium' || passwordStrength === 'strong'} $strength={passwordStrength} />
                  <StrengthBar $active={passwordStrength === 'strong'} $strength={passwordStrength} />
                </PasswordStrength>
              )}

              <InputGroup $delay={1} $hasError={error && password !== confirmPassword}>
                <StyledInput
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  $hasError={error && password !== confirmPassword}
                  required
                />
                <InputIcon>
                  <RiLockPasswordLine />
                </InputIcon>
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </PasswordToggle>
              </InputGroup>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? t('resetPassword.submitting') : t('resetPassword.submitButton')}
              </SubmitButton>
            </Form>

            {(error || message) && (
              <MessageBox $success={isSuccess}>
                <MessageIcon $success={isSuccess}>
                  {isSuccess ? (
                    <SuccessCheckmark viewBox="0 0 24 24">
                      <path d="M5 12l5 5L20 7" />
                    </SuccessCheckmark>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  )}
                </MessageIcon>
                <MessageText>{error || message}</MessageText>
              </MessageBox>
            )}
          </FormContainer>
        </FormSide>
      </Card>
    </PageContainer>
  );
};

export default RedefineSenhaPage;
